// Theme controller. Source of truth is <html class="dark">; localStorage
// `theme` carries an explicit user override ('light' | 'dark') and `null`
// / missing means "follow OS preference".

import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

function readStoredPreference(): ThemePreference {
  try {
    const v = localStorage.getItem('theme');
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* noop */
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  );
}

function apply(pref: ThemePreference): ResolvedTheme {
  const resolved: ResolvedTheme =
    pref === 'light' ? 'light' : pref === 'dark' ? 'dark' : systemPrefersDark() ? 'dark' : 'light';
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  return resolved;
}

export function useTheme(): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (p: ThemePreference) => void;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => apply(preference));

  const setPreference = useCallback((p: ThemePreference) => {
    try {
      if (p === 'system') localStorage.removeItem('theme');
      else localStorage.setItem('theme', p);
    } catch {
      /* noop */
    }
    setPreferenceState(p);
    setResolved(apply(p));
  }, []);

  // When the user is on 'system', react to OS changes live.
  useEffect(() => {
    if (preference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      setResolved(apply('system'));
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  return { preference, resolved, setPreference };
}
